<template>
  <ion-content class="modal-wrapper ion-padding">
    <ion-row>
      <ion-col size="12">
        <h4 class="h3 text-color-body text-align-center">
          {{ t('files.upload', { title: t('files.' + getDocumentSelected.name.toLowerCase()) }) }}
        </h4>
      </ion-col>
      <ion-col size="12">
        <section class="message-wrapper mt-xs">
          <ion-icon :icon="informationCircleOutline" color="primary"></ion-icon>
          <ion-label class="text-color-placeholder text-xs"
            >{{ t('files.fileDescription') }}
          </ion-label>
        </section>
      </ion-col>
    </ion-row>
    <div v-for="(inp, idxInput) in getDocumentSelected?.Inputs" :key="idxInput">
      <div v-if="inp.type === 'file' && inp.max" @click="inputSelected = inp">
        <UploadPhoto
          :file-type="inp.type"
          :label="inp.label"
          v-model="file"
          :disabled="inp.Answers.length >= inp.max"
        />

        <section v-if="file.size > 0" class="file-wrapper">
          <ion-row class="d-flex ion-align-items-center ion-justify-content-center">
            <ion-col size="3">
              <ProgressWrapper :show-data="false" :current-step="1" :total-steps="2" />
            </ion-col>
            <ion-col size="8">
              <h4 class="h4 text-color-placeholder text-weight-bold text-xs text-align-left">
                {{ file.title }}
              </h4>
              <p class="text-color-placeholder text-xs text-align-left">
                {{ fileSize(file.size) }}
              </p>
            </ion-col>
            <ion-col size="1">
              <ion-icon :icon="trash" @click="deleteFile" />
            </ion-col>
          </ion-row>
        </section>

        <div v-for="(imageAnswer, idxAnswer) in inp.Answers" :key="idxAnswer">
          <ion-row v-if="getDocumentSelected" class="ba-grey pa-xs border-default document-card">
            <ion-col size="12" class="ion-no-padding d-flex justify-space-between">
              <div class="d-flex">
                <img class="border-default" :src="imageAnswer.value" alt="" />
                <div class="pl-xs">
                  <p class="font-sm">{{ getDocumentSelected.name }} #{{ idxAnswer + 1 }}</p>
                  <p v-if="imageAnswer.size" class="font-sm grey text-start">
                    {{ imageAnswer.size }} KB
                  </p>
                </div>
              </div>

              <ion-button
                @click="handleDeleteInput(idxInput, idxAnswer)"
                color="primary"
                shape="round"
                size="small"
                fill="clear"
                class="ion-no-padding"
              >
                <ion-icon color="grey" :icon="trashBin"></ion-icon>
              </ion-button>
            </ion-col>
          </ion-row>
        </div>
      </div>
      <div v-if="inp.Answers[0] && inp.type === 'date'" class="mt-xs">
        <CustomInput
          :min-date="dayjs().format('YYYY-MM-DD')"
          :value="inp.Answers[0].value"
          v-model="inp.Answers[0].value"
          form-name="value"
          type="date"
          input-type="date"
          :label="inp.label"
          name="departure"
          :placeholder="inp.label"
          :clear-field="false"
        />
      </div>
    </div>
    <div class="d-flex justify-space-between mt-xs">
      <ion-button
        fill="clear"
        expand="block"
        size="default"
        color="primary"
        class="text-lg text-weight-bold"
        @click="emit('close')"
      >
        {{ t('actions.cancel') }}
      </ion-button>

      <ion-button
        :disabled="!getDisabled || loading"
        fill="solid"
        expand="block"
        size="default"
        color="primary"
        class="text-lg text-weight-bold"
        @click="handleCreateDocument()"
      >
        {{
          loading
            ? t('actions.uploading')
            : `${t('actions.add')} ${t('files.' + getDocumentSelected.name.toLowerCase())}`
        }}
      </ion-button>
    </div>
  </ion-content>
</template>

<script setup lang="ts">
  import { IonButton, IonIcon, IonContent, IonRow, IonCol, IonLabel } from '@ionic/vue';
  import { informationCircleOutline, trash, trashBin } from 'ionicons/icons';
  import { computed, onMounted, PropType, ref, watch } from 'vue';
  import { IDocument } from '@/interfaces/profile/profile-document.interface';
  import { useI18n } from 'vue-i18n';
  import UploadPhoto from '@/components/shared/UploadPhoto.vue';
  import { IUploadImage } from '@/interfaces/IUploadImage';
  import ProgressWrapper from '@/components/shared/ProgressWrapper.vue';
  import { fileSize } from '@/utils/global-utils';
  import CustomInput from '@/components/shared/form/CustomInput.vue';
  import { Answer } from '@/interfaces/tenants/documents.interface';
  import { useToast } from '@/composables/useToast';
  import { useProfileStore } from '@/stores/profile-store';
  import dayjs from 'dayjs';
  import { DocumentForm } from '@/interfaces/profile/profile-response.interface';

  const { deleteAnswerInput, saveDocuments, getDocuments } = useProfileStore();
  const emit = defineEmits(['close']);
  const { showToast } = useToast();

  const { t } = useI18n();

  const inputSelected = ref();
  const loading = ref<boolean>(false);

  const props = defineProps({
    getDocumentSelected: {
      type: Object as PropType<IDocument>,
      required: true,
    },
  });

  const toggleUploadPhoto = ref(false);
  const file = ref<IUploadImage>({
    base64: '',
    title: '',
    size: 0,
  });

  const deleteFile = () => {
    file.value = {
      base64: '',
      title: '',
      size: 0,
    };
  };

  const getDisabled = computed(() => {
    if (!props.getDocumentSelected) {
      return true;
    }
    let isValid = true;

    props.getDocumentSelected.Inputs.forEach(({ type, Answers, min, max }) => {
      if (type === 'date') {
        if (!Answers || Answers.length === 0 || Answers[0].value === '') {
          isValid = false;
        }
      } else if (type === 'file' && min && max) {
        if (Answers.length < min || Answers.length > max) {
          isValid = false;
        }
      }
    });

    return isValid;
  });

  const setNewDocumentFile = () => {
    inputSelected.value.Answers.push({
      DocumentsMasterInputId: inputSelected.value.id,
      value: file.value.base64,
      size: file.value.size,
    });
    file.value.base64 = '';
    file.value.size = 0;
  };

  const transformAnswers = (data: IDocument) => {
    return data.Inputs.flatMap(({ Answers }) =>
      Answers.map(({ DocumentsMasterInputId, value, id }) => {
        const transformedObject: Answer = {
          DocumentsMasterInputId,
          value,
        };
        if (id !== undefined) {
          transformedObject.id = id;
        }
        return transformedObject;
      }),
    );
  };

  const handleCreateDocument = async () => {
    try {
      loading.value = true;

      const transformedArray = transformAnswers(props.getDocumentSelected as IDocument);

      const form: DocumentForm = {
        DocumentsMasterId: Number(props.getDocumentSelected?.id),
        answers: transformedArray,
      };

      const response = await saveDocuments(form);
      if (response.success) {
        toggleUploadPhoto.value = false;
        emit('close');
        await showToast(response.msg as string);
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      await getDocuments();
      loading.value = false;
    }
  };

  const spliceDocument = (idxInput: number, idxAnswer: number) => {
    // eslint-disable-next-line vue/no-mutating-props
    props.getDocumentSelected?.Inputs[idxInput].Answers.splice(idxAnswer, 1);
  };

  const deleteAnswer = async (id: string, idxInput: number, idxAnswer: number) => {
    try {
      loading.value = true;
      const response = await deleteAnswerInput(id);
      if (response.success) {
        await showToast(response.msg as string);
        await spliceDocument(idxInput, idxAnswer);
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      await getDocuments();
      loading.value = false;
    }
  };

  const handleDeleteInput = (idxInput: number, idxAnswer: number) => {
    if (props.getDocumentSelected?.Inputs[idxInput].Answers[idxAnswer].id) {
      deleteAnswer(
        props.getDocumentSelected?.Inputs[idxInput].Answers[idxAnswer].id,
        idxInput,
        idxAnswer,
      );
    } else {
      spliceDocument(idxInput, idxAnswer);
    }
    deleteFile();
    toggleUploadPhoto.value = false;
  };

  const initialConfig = () => {
    createAnswerObject();
  };

  const createAnswerObject = () => {
    const dateInputs = props.getDocumentSelected?.Inputs.filter((input) => input.type === 'date');

    if (dateInputs) {
      dateInputs.forEach((input) => {
        if (input.Answers.length === 0) {
          input.Answers.push({
            DocumentsMasterInputId: input.id,
            value: '',
          });
        } else if (!input.Answers[0].id) {
          input.Answers = [];
          input.Answers.push({
            DocumentsMasterInputId: input.id,
            value: '',
          });
        }
      });
    }
  };

  onMounted(() => {
    initialConfig();
  });

  watch(file, (val) => {
    if (val.base64 !== '') {
      setNewDocumentFile();
    }
  });
</script>
<style scoped lang="scss">
  .document-wrapper {
    position: relative;

    &__status {
      position: absolute;
      top: 10px;
      left: 10px;
      z-index: 2;
    }
  }

  ion-content.modal-wrapper {
    --border-radius: 25px;
    --background: white;
  }

  .file-wrapper {
    margin-top: 20px;
    margin-bottom: 30px;
    padding: 10px;
    border-radius: 5px;
    border: 1px solid #d4ddff;
  }

  .show-file-wrapper {
    position: relative;

    img {
      object-fit: cover;
      height: 170px;
      width: 100%;
    }

    &__menu {
      position: absolute;
      top: 10px;
      right: 10px;
    }
  }

  .upload-wrapper {
    position: relative;
    height: 170px;
    background: #d4ddff66;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    &__icon {
      display: flex;
      justify-items: center;
      align-items: center;

      img {
        width: 30px;
        height: 30px;
        margin: 0 auto;
      }
    }

    &__menu {
      position: absolute;
      top: 10px;
      right: 10px;
    }

    &__preview {
      height: 170px;
      width: 100%;
      background: white;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;

      &__icon {
        width: 100%;
        display: block;
      }

      img {
        width: 100%;
        height: 190px;
        object-fit: cover;
      }

      h1 {
        font-size: 30px;
        font-weight: bold;
        color: white;
      }
    }
  }

  .document-card {
    img {
      width: 44px;
      object-fit: cover;
      height: 44px;
    }
  }

  .modal-wrapper {
    &__pdf {
      img {
        width: 45px;
        height: 40px;
        border-radius: 10px;
      }
    }
  }
</style>
